import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    bulkDeleteAccountGroupResponseItemSchema,
    bulkDeleteAccountGroupsBodySchema,
    createAccountGroupBodySchema,
    entityAccountGroupSchema,
    updateAccountGroupBodySchema,
} from "@common/schemas/domains/accounting/account-group.schema";

const c = initContract();

export const accountGroupContract = c.router(
    {
        entityList: {
            method: "GET",
            path: "/entities/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: z.array(entityAccountGroupSchema),
                404: ErrorMessageSchema,
            },
            summary: "List entity account groups",
            description: "Requires entity read access.",
        },
        entityGet: {
            method: "GET",
            path: "/entities/:entityId/:id",
            pathParams: z.object({
                entityId: z.coerce.number(),
                id: z.string().uuid(),
            }),
            responses: {
                200: entityAccountGroupSchema,
                404: ErrorMessageSchema,
            },
            summary: "Get an entity account group",
            description: "Requires entity read access.",
        },
        entityCreate: {
            method: "POST",
            path: "/entities/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: createAccountGroupBodySchema,
            responses: {
                201: entityAccountGroupSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            summary: "Create an entity account group",
            description:
                "Requires entity access and chart-of-accounts edit permission.",
        },
        entityUpdate: {
            method: "PATCH",
            path: "/entities/:entityId/:id",
            pathParams: z.object({
                entityId: z.coerce.number(),
                id: z.string().uuid(),
            }),
            body: updateAccountGroupBodySchema,
            responses: {
                200: entityAccountGroupSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            summary: "Update an entity account group",
            description:
                "Requires entity access and chart-of-accounts edit permission.",
        },
        entityDeleteBulk: {
            method: "POST",
            path: "/bulk-delete",
            body: bulkDeleteAccountGroupsBodySchema,
            responses: {
                200: z.array(bulkDeleteAccountGroupResponseItemSchema),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            summary: "Delete entity account groups across businesses",
            description:
                "Deletes each (groupId, entityId) group in one transaction, lifting children one level. Requires entity access and chart-of-accounts edit permission.",
        },
        entityDelete: {
            method: "DELETE",
            path: "/entities/:entityId/:id",
            pathParams: z.object({
                entityId: z.coerce.number(),
                id: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            summary: "Delete an entity account group",
            description:
                "Requires entity access and chart-of-accounts edit permission.",
        },
    },
    {
        pathPrefix: "/accounting/account-groups",
    },
);
